/** 値の範囲 `[min, max]`。 */
export interface Domain {
  readonly min: number;
  readonly max: number;
}

/**
 * 見やすい目盛りの刻み幅（1・2・5 × 10^n）。目盛りがおよそ `count` 本になるものを選ぶ。
 */
export function niceStep(range: number, count: number): number {
  if (!(range > 0) || count < 1) return 1;
  const raw = range / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  // 幾何平均を境に、近い値へ丸める（1・2・5・10）
  const factor =
    normalized >= Math.sqrt(50)
      ? 10
      : normalized >= Math.sqrt(10)
        ? 5
        : normalized >= Math.sqrt(2)
          ? 2
          : 1;
  return factor * magnitude;
}

/** 範囲をおおうキリのよい目盛り（昇順）と、目盛りを含む範囲。 */
export function niceTicks(domain: Domain, count: number): { ticks: number[]; domain: Domain } {
  const { min, max } = domain;
  if (!Number.isFinite(min) || !Number.isFinite(max))
    return { ticks: [0], domain: { min: 0, max: 1 } };
  // 幅 0（一定値）のときは、前後に余白を作る
  const lo = min === max ? min - 1 : min;
  const hi = min === max ? max + 1 : max;
  const step = niceStep(hi - lo, count);
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step * 1e-9; value += step) {
    // 浮動小数の誤差を丸める（0.30000000000000004 など）
    ticks.push(Number(value.toFixed(10)));
  }
  return { ticks, domain: { min: start, max: end } };
}

/** `value` を `domain` から `[from, to]` へ線形に写す。 */
export function mapLinear(value: number, domain: Domain, from: number, to: number): number {
  const span = domain.max - domain.min;
  return span === 0 ? from : from + ((value - domain.min) / span) * (to - from);
}

/** 系列の最小・最大。空なら `{ min: 0, max: 1 }`。 */
export function extent(values: readonly number[]): Domain {
  if (values.length === 0) return { min: 0, max: 1 };
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return { min, max };
}

/** SVG の折れ線のパス（`M x y L x y …`）。点が無ければ空文字。座標は小数 2 桁に丸める。 */
export function linePath(points: ReadonlyArray<readonly [number, number]>): string {
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
}
